// Picks the inline editor for a custom field's type. CustomFieldEditorProps is the contract every one of
// the editors implements.
import React from 'react';
import type {CustomFieldColumnDef} from '../../../../shared/components/custom-field-columns/customFieldColumnModel';
import type {OpportunityNode} from '../../../opportunityTypes';
import type {HyperlinkProblem} from '../../../../shared/util/customFieldValueValidation';
import {TextFieldEditor} from './TextFieldEditor';
import {NumberFieldEditor} from './NumberFieldEditor';
import {DateFieldEditor} from './DateFieldEditor';
import {DropdownFieldEditor} from './DropdownFieldEditor';
import {BooleanFieldEditor} from './BooleanFieldEditor';
import {HyperlinkFieldEditor} from './HyperlinkFieldEditor';
import {CurrencyFieldEditor} from './CurrencyFieldEditor';
import {MultiSelectFieldEditor} from './MultiSelectFieldEditor';

// Localized copy, resolved once per page render.
export interface CustomFieldEditorLabels {
	update: string;
	cancel: string;
	none: string;
	integerInvalid: string;
	decimalInvalid: string;
	// Why an amount cannot be saved: more digits than a Currency field holds.
	amountInvalid: string;
	// The row that ticks or clears every option of a Multi-select.
	selectAll: string;
	yes: string;
	no: string;
	// Why a link cannot be saved, one message per reason.
	hyperlinkProblems: Record<HyperlinkProblem, string>;
	openLink: string;
}

export interface CustomFieldEditorProps {
	node: OpportunityNode;
	def: CustomFieldColumnDef;
	// The stored value in its stored form (a dropdown's is the option key, a date's is ISO), or null.
	current: string | null;
	// The page's retry(): called once a save succeeds, so the cell shows the new value after the refetch.
	onCommit: () => void;
	// Dismisses the popover.
	close: () => void;
	labels: CustomFieldEditorLabels;
	// The viewer's decimal separator, from their profile language (decimalSeparatorFor). A decimal is
	// shown and typed with it and stored with '.', as on the record page.
	decimalSeparator: ',' | '.';
}

export const CustomFieldEditor = (props: CustomFieldEditorProps): JSX.Element => {
	switch (props.def.kind) {
		case 'DROPDOWN':
			return <DropdownFieldEditor {...props} />;
		case 'INTEGER':
		case 'DECIMAL':
			return <NumberFieldEditor {...props} />;
		case 'DATE':
			return <DateFieldEditor {...props} />;
		case 'BOOLEAN':
			return <BooleanFieldEditor {...props} />;
		case 'HYPERLINK':
			return <HyperlinkFieldEditor {...props} />;
		case 'CURRENCY':
			return <CurrencyFieldEditor {...props} />;
		case 'MULTI_SELECT':
			return <MultiSelectFieldEditor {...props} />;
		default:
			return <TextFieldEditor {...props} />;
	}
};

export default CustomFieldEditor;
